import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { enUS, nb, pl, sv } from "date-fns/locale";
import { currentDateFnsLocale } from "../../../i18n/dateLocales";
import { fetchHours, fetchHour, exportHoursPdf, exportHoursExcel } from "../../../api/hour";
import { fetchUser } from "../../../api/users";
import { hourHasProjectActivity, projectActivityLine } from "./projectActivityDisplay";
import { EmployeeSelector } from "../../utils/moduleSelectors/employeeSelector";
import {
  mergeHoursApprovalSearchParams,
  parseHoursApprovalSearchParams,
  shouldDropEmployeeFromUserFetch,
  userFromFetchResponse,
} from "./hoursApprovalSearchParams";
import { ApproveHourDialog } from "./approveHourDialog";
import { HourEditDialog } from "./hour_edit_dialog";
import { PeriodSelector } from "./period_selector";
import { CheckCircleIcon, XCircleIcon, ChevronDownIcon, ChevronUpIcon, ArrowPathIcon } from "@heroicons/react/24/outline";
import { toast } from "react-hot-toast";
import { updateHourAsync } from "../../../slices/hour";
import { PMDialog } from "../../utils/PMDialog";
import { Form, Formik } from "formik";
import { PMField } from "../../utils/PMField";
import { PMButton } from "../../utils/PMButton";
import * as Yup from "yup";
import {
  useCanApproveHours,
  useCanBulkReassignHours,
  useCanConfirmAgreedOvertime,
} from "../../../routes/roleOnly";
import { HoursBulkReassignDialog } from "./hours_bulk_reassign_dialog";
import { ProjectSelector } from "../../utils/moduleSelectors/projectSelector";
import { AgreedOvertimeBadge } from "./AgreedOvertimeBadge";
import { HourBillingBadges } from "./HourBillingPanel";
import { HourBillingButton, useCanReconcileHours } from "./HourBillingDialog";
import {
  HOURS_APPROVAL_DETAIL_FIELDS,
  HOURS_APPROVAL_LIST_FIELDS,
} from "./hoursApprovalFields";

const StatusBadge = ({ status }) => {
  const colorMap = {
    APPROVED: "bg-green-100 text-green-800",
    NOT_APPROVED: "bg-red-100 text-red-800",
    UPDATED: "bg-amber-100 text-amber-800",
    PROCESSED: "bg-blue-100 text-blue-800",
    NEW: "bg-gray-100 text-gray-800",
  };
  const labelMap = {
    APPROVED: "Godkjent",
    NOT_APPROVED: "Ikke godkjent",
    UPDATED: "Oppdatert",
    PROCESSED: "Behandlet",
    NEW: "Ny",
  };
  const color = colorMap[status] || colorMap.NEW;
  return (
    <span className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ${color}`}>
      {labelMap[status] || status}
    </span>
  );
};

const TripletexSyncBadge = ({ hour }) => {
  const { t } = useTranslation();
  const isSynced = hour.tripletex_entry_id && hour.last_synced_at;
  const isOutdated = isSynced && hour.updated_at && new Date(hour.updated_at) > new Date(hour.last_synced_at);
  const ready = hour.tripletex_sync_ready === true;
  const blockHint =
    hour.tripletex_sync_block_reason_message ||
    hour.tripletex_sync_block_reason ||
    "";

  if (isSynced && !isOutdated) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700" title={t("Synkronisert med Tripletex")}>
        <CheckCircleIcon className="h-3 w-3" />
        <span>{t("Synkronisert")}</span>
      </span>
    );
  }

  // Synket til Tripletex men raden er oppdatert etter siste push — aldri vis resolver/«legacy» her.
  if (isSynced && isOutdated) {
    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700"
        title={t("Timen er endret etter siste Tripletex-synk og kan synkes på nytt")}
      >
        <ArrowPathIcon className="h-3 w-3" />
        <span>{t("Kan resynkes")}</span>
      </span>
    );
  }

  if (ready) {
    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700"
        title={t("Timen er klar for Tripletex-synk")}
      >
        <ArrowPathIcon className="h-3 w-3" />
        <span>{t("Kan synkes")}</span>
      </span>
    );
  }

  return (
    <span
      className="inline-flex max-w-[220px] items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800"
      title={blockHint ? `${t("Kan ikke synkes til Tripletex")}: ${blockHint}` : t("Kan ikke synkes til Tripletex")}
    >
      <XCircleIcon className="h-3 w-3 shrink-0" />
      <span className="truncate">{blockHint || t("Kan ikke synkes")}</span>
    </span>
  );
};

const REPORT_PATH = (reportId) => `/dashboard/service/reports/${reportId}`;
const WORK_ORDER_PATH = (workOrderId) => `/dashboard/work_orders/${workOrderId}`;

const getReportTitle = (report) =>
  report?.displayTitle || report?.report_number || report?.title || report?.name || "-";

const getWorkOrderTitle = (workOrder) =>
  workOrder?.displayTitle || workOrder?.title || workOrder?.number || workOrder?.name || "-";

const hourDayKey = (hour) => {
  if (!hour?.date) return "";
  try {
    return format(new Date(hour.date), "yyyy-MM-dd");
  } catch {
    return "";
  }
};

const parseHourTime = (hour) => parseFloat(hour?.time) || 0;

const ModuleStatusBadge = ({ status }) => {
  if (!status) return null;

  const colorMap = {
    draft: "bg-gray-100 text-gray-700",
    ongoing: "bg-green-100 text-green-700",
    finished: "bg-emerald-100 text-emerald-700",
    processing: "bg-amber-100 text-amber-700",
    processed: "bg-blue-100 text-blue-700",
    cancelled: "bg-red-100 text-red-700",
    ready_invoice: "bg-orange-100 text-orange-700",
    invoiced: "bg-indigo-100 text-indigo-700",
    rejected: "bg-red-100 text-red-700",
    approved: "bg-green-100 text-green-700",
    updated: "bg-sky-100 text-sky-700",
  };

  const labelMap = {
    draft: "Kladd",
    ongoing: "Pågår",
    finished: "Ferdig",
    processing: "Behandles",
    processed: "Behandlet",
    cancelled: "Avbrutt",
    ready_invoice: "Klar for faktura",
    invoiced: "Fakturert",
    rejected: "Avvist",
    approved: "Godkjent",
    updated: "Oppdatert",
  };

  const normalizedStatus = String(status).toLowerCase();

  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${colorMap[normalizedStatus] || "bg-gray-100 text-gray-700"}`}>
      {labelMap[normalizedStatus] || status}
    </span>
  );
};

const ModuleApprovalBadge = ({ approved, notApproved, approvalStatus }) => {
  let label = "Ikke behandlet";
  let classes = "bg-gray-100 text-gray-700";

  if (approvalStatus === "APPROVED" || approved) {
    label = "Godkjent";
    classes = "bg-green-100 text-green-700";
  } else if (approvalStatus === "NOT_APPROVED" || notApproved) {
    label = "Ikke godkjent";
    classes = "bg-red-100 text-red-700";
  } else if (approvalStatus === "PENDING_APPROVAL") {
    label = "Til godkjenning";
    classes = "bg-amber-100 text-amber-700";
  }

  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${classes}`}>
      {label}
    </span>
  );
};

export const HoursApprovalList = () => {
  const { t, i18n } = useTranslation();
  const canApproveHours = useCanApproveHours();
  const canConfirmOt = useCanConfirmAgreedOvertime();
  const canProcess = canApproveHours || canConfirmOt;
  const canBulkReassign = useCanBulkReassignHours();
  const canReconcile = useCanReconcileHours();

  const [searchParams, setSearchParams] = useSearchParams();
  const approvalFilters = useMemo(
    () => parseHoursApprovalSearchParams(searchParams),
    [searchParams]
  );
  const { employeeId, period: periodType, view: viewFilter, dateFrom, dateTo } = approvalFilters;

  const [selectedUser, setSelectedUser] = useState(null);
  const [projectFilter, setProjectFilter] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hours, setHours] = useState([]);

  const [processOpen, setProcessOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [selectedHour, setSelectedHour] = useState(null);
  const [selectedHours, setSelectedHours] = useState(new Set());
  const [expandedHours, setExpandedHours] = useState(new Set());
  const [hourDetails, setHourDetails] = useState({});
  const [loadingDetails, setLoadingDetails] = useState(new Set());
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [exporting, setExporting] = useState(null);
  const [notApprovedDialogOpen, setNotApprovedDialogOpen] = useState(false);
  const [bulkReassignOpen, setBulkReassignOpen] = useState(false);
  const dispatch = useDispatch();
  /** Bumps on each load() so an older in-flight response cannot overwrite newer state (race when filters change). */
  const loadGenerationRef = useRef(0);
  const hydratedEmployeeRef = useRef(null);

  const patchApprovalParams = useCallback(
    (patch, { replace = false } = {}) => {
      setSearchParams((prev) => mergeHoursApprovalSearchParams(prev, patch), { replace });
    },
    [setSearchParams]
  );

  useEffect(() => {
    if (!employeeId) {
      setSelectedUser(null);
      hydratedEmployeeRef.current = null;
      return;
    }
    if (hydratedEmployeeRef.current === employeeId) return;

    let cancelled = false;
    (async () => {
      try {
        const res = await fetchUser(employeeId, { fields: "id,displayTitle,user_type_id" });
        if (cancelled) return;
        if (shouldDropEmployeeFromUserFetch(res)) {
          hydratedEmployeeRef.current = null;
          setSelectedUser(null);
          patchApprovalParams({ employee: null }, { replace: true });
          return;
        }
        const user = userFromFetchResponse(res);
        if (user?.id == null) {
          // Transient (network / 401 / 5xx): keep employee in the URL.
          return;
        }
        hydratedEmployeeRef.current = employeeId;
        setSelectedUser(user);
      } catch {
        // fetchUser swallows axios errors; keep the shared employee param.
        return;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [employeeId, patchApprovalParams]);

  const summaryDateLocale = useMemo(() => {
    const lang = (i18n.language || "no").split("-")[0];
    if (lang === "en") return enUS;
    if (lang === "sv" || lang === "se") return sv;
    if (lang === "pl") return pl;
    return nb;
  }, [i18n.language]);

  // Only the not_approved tab changes API filters; pending / approved / all share the same fetch.
  const serverNotApprovedOnly = viewFilter === "not_approved";

  const controller = useMemo(() => {
    const filters = [];
    if (employeeId) filters.push({ name: "USER", value: employeeId });
    if (projectFilter?.id) filters.push({ name: "PROJECT", value: projectFilter.id });
    // Server-side filter only for not_approved to reduce payload when that view selected
    if (serverNotApprovedOnly) filters.push({ name: "NOT_APPROVED", value: true });
    return {
      date_from: dateFrom,
      date_to: dateTo,
      page: 1,
      per_page: 500,
      sort: "DESC",
      sortBy: "date",
      filters,
      view: "approval",
      fields: HOURS_APPROVAL_LIST_FIELDS,
    };
  }, [employeeId, projectFilter?.id, dateFrom, dateTo, serverNotApprovedOnly]);

  const exportParams = useMemo(() => {
    const filters = [];
    if (employeeId) filters.push({ name: "USER", value: employeeId });
    if (projectFilter?.id) filters.push({ name: "PROJECT", value: projectFilter.id });
    if (viewFilter === "not_approved") filters.push({ name: "NOT_APPROVED", value: true });
    if (viewFilter === "approved") filters.push({ name: "APPROVED", value: true });
    if (viewFilter === "pending") filters.push({ name: "PENDING", value: true });
    return {
      date_from: dateFrom,
      date_to: dateTo,
      filters,
      view: "approval",
    };
  }, [employeeId, projectFilter?.id, dateFrom, dateTo, viewFilter]);

  const handleExport = useCallback(
    async (kind) => {
      if (exporting) return;
      setExporting(kind);
      try {
        if (kind === "pdf") {
          await exportHoursPdf(exportParams);
          toast.success(t("hours.sheet.export_pdf_success"));
        } else {
          await exportHoursExcel(exportParams);
          toast.success(t("hours.sheet.export_excel_success"));
        }
      } catch {
        toast.error(t("hours.sheet.export_error"));
      } finally {
        setExporting(null);
      }
    },
    [exporting, exportParams, t]
  );

  const load = useCallback(async () => {
    const gen = ++loadGenerationRef.current;
    setIsLoading(true);
    try {
      const res = await fetchHours(controller);
      if (gen !== loadGenerationRef.current) return;
      setHours(Array.isArray(res?.data) ? res.data : []);
    } catch (e) {
      // noop
    } finally {
      if (gen === loadGenerationRef.current) {
        setIsLoading(false);
      }
    }
  }, [controller]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    if (!Array.isArray(hours)) return [];
    switch (viewFilter) {
      case "pending":
        return hours.filter((h) => !h.approved && !h.not_approved);
      case "approved":
        return hours.filter((h) => !!h.approved);
      case "not_approved":
        return hours.filter((h) => !!h.not_approved);
      default:
        return hours;
    }
  }, [hours, viewFilter]);

  const approvalSelectionSummary = useMemo(() => {
    if (!selectedHours.size) return null;

    const selectedRows = filtered.filter((h) => h?.id != null && selectedHours.has(h.id));
    if (!selectedRows.length) return null;

    const periodByUser = new Map();
    for (const h of hours) {
      const uid = h.user?.id;
      if (uid == null) continue;
      const dk = hourDayKey(h);
      const tm = parseHourTime(h);
      if (!periodByUser.has(uid)) {
        periodByUser.set(uid, { byDay: new Map(), periodHours: 0, periodEntries: 0, user: h.user });
      }
      const st = periodByUser.get(uid);
      st.periodHours += tm;
      st.periodEntries += 1;
      if (dk) {
        const cur = st.byDay.get(dk) || { hours: 0, entries: 0 };
        cur.hours += tm;
        cur.entries += 1;
        st.byDay.set(dk, cur);
      }
    }

    const selectedByUser = new Map();
    for (const h of selectedRows) {
      const uid = h.user?.id ?? "__none__";
      const dk = hourDayKey(h);
      const tm = parseHourTime(h);
      if (!selectedByUser.has(uid)) {
        selectedByUser.set(uid, { user: h.user, byDay: new Map(), selectedHours: 0, selectedEntries: 0 });
      }
      const su = selectedByUser.get(uid);
      su.selectedHours += tm;
      su.selectedEntries += 1;
      if (dk) {
        const cur = su.byDay.get(dk) || { selectedHours: 0, selectedEntries: 0 };
        cur.selectedHours += tm;
        cur.selectedEntries += 1;
        su.byDay.set(dk, cur);
      }
    }

    const users = Array.from(selectedByUser.entries()).map(([uid, sel]) => {
      const period = periodByUser.get(uid) || { byDay: new Map(), periodHours: 0, periodEntries: 0, user: sel.user };
      const dayRows = Array.from(sel.byDay.entries())
        .map(([dk, s]) => {
          const reg = period.byDay.get(dk) || { hours: 0, entries: 0 };
          let dateLabel = dk;
          try {
            dateLabel = format(new Date(`${dk}T12:00:00`), "d. MMM yyyy", { locale: summaryDateLocale });
          } catch {
            /* keep dk */
          }
          return {
            dayKey: dk,
            dateLabel,
            selectedHours: s.selectedHours,
            registeredHours: reg.hours,
            registeredEntries: reg.entries,
          };
        })
        .sort((a, b) => a.dayKey.localeCompare(b.dayKey));

      let otherDays = 0;
      let otherHours = 0;
      for (const [dk, reg] of period.byDay) {
        if (!sel.byDay.has(dk)) {
          otherDays += 1;
          otherHours += reg.hours;
        }
      }

      const title =
        uid === "__none__"
          ? t("hours.approval.selection_summary.unknown_user")
          : sel.user?.displayTitle || sel.user?.title || `User #${uid}`;

      return {
        userId: uid,
        title,
        selectedHours: sel.selectedHours,
        selectedEntries: sel.selectedEntries,
        periodHours: period.periodHours,
        periodEntries: period.periodEntries,
        dayRows,
        otherDays,
        otherHours,
      };
    });

    users.sort((a, b) => a.title.localeCompare(b.title, undefined, { sensitivity: "base" }));

    return { users };
  }, [filtered, hours, selectedHours, summaryDateLocale, t]);

  const handleHourUpdated = useCallback((updated) => {
    if (!updated?.id) return;
    setHours((prev) => {
      if (!Array.isArray(prev)) return prev;
      const idx = prev.findIndex((h) => h?.id === updated.id);
      if (idx === -1) return prev;
      const next = prev.slice();
      next[idx] = { ...next[idx], ...updated };
      return next;
    });
    // Update details cache if exists
    if (hourDetails[updated.id]) {
      setHourDetails((prev) => ({
        ...prev,
        [updated.id]: { ...prev[updated.id], ...updated },
      }));
    }
    setProcessOpen(false);
    setSelectedHour(null);
    // Remove from selection
    setSelectedHours((prev) => {
      const next = new Set(prev);
      next.delete(updated.id);
      return next;
    });
  }, [hourDetails]);

  const handleHourEditSaved = useCallback((updated) => {
    if (!updated?.id) {
      load();
      setEditOpen(false);
      setSelectedHour(null);
      return;
    }

    handleHourUpdated(updated);
    setEditOpen(false);
  }, [handleHourUpdated, load]);

  const handleHourDeleted = useCallback((deletedHourId) => {
    if (!deletedHourId) {
      load();
    } else {
      setHours((prev) => prev.filter((hour) => hour?.id !== deletedHourId));
      setHourDetails((prev) => {
        const next = { ...prev };
        delete next[deletedHourId];
        return next;
      });
      setExpandedHours((prev) => {
        const next = new Set(prev);
        next.delete(deletedHourId);
        return next;
      });
      setSelectedHours((prev) => {
        const next = new Set(prev);
        next.delete(deletedHourId);
        return next;
      });
    }

    setEditOpen(false);
    setSelectedHour(null);
  }, [load]);

  // Toggle accordion; fetch only extra fields (list row already has user/project/project_activity/report/work_order)
  const toggleAccordion = useCallback((hour) => {
    const hourId = hour?.id;
    if (!hourId) return;
    let opening = false;
    setExpandedHours((prev) => {
      const next = new Set(prev);
      opening = !next.has(hourId);
      if (opening) next.add(hourId);
      else next.delete(hourId);
      return next;
    });
    if (!opening) return;
    if (hourDetails[hourId] || loadingDetails.has(hourId)) return;
    setLoadingDetails((ld) => new Set(ld).add(hourId));
    fetchHour(hourId, {
      params: {
        view: "approval",
        fields: HOURS_APPROVAL_DETAIL_FIELDS,
      },
    })
      .then((res) => {
        setHourDetails((d) => ({
          ...d,
          [hourId]: { ...hour, ...(res.data || {}) },
        }));
      })
      .catch((err) => {
        console.error("Failed to fetch hour details:", err);
        toast.error("Kunne ikke laste detaljer");
      })
      .finally(() => {
        setLoadingDetails((ld) => {
          const n = new Set(ld);
          n.delete(hourId);
          return n;
        });
      });
  }, [hourDetails, loadingDetails]);

  // Toggle selection
  const toggleSelection = useCallback((hourId) => {
    setSelectedHours((prev) => {
      const next = new Set(prev);
      if (next.has(hourId)) {
        next.delete(hourId);
      } else {
        next.add(hourId);
      }
      return next;
    });
  }, []);

  // Toggle all selection
  const toggleAllSelection = useCallback(() => {
    if (selectedHours.size === filtered.length) {
      setSelectedHours(new Set());
    } else {
      setSelectedHours(new Set(filtered.map((h) => h.id)));
    }
  }, [selectedHours.size, filtered]);

  // Bulk approve — sequential + unwrap so rejected thunks never look like full success.
  const handleBulkApprove = useCallback(async (approved = true, note = null) => {
    if (selectedHours.size === 0) {
      toast.error(t("hours.approval.bulk.select_required"));
      return;
    }

    // Prevent double-clicking
    if (bulkProcessing) {
      return;
    }

    setBulkProcessing(true);

    const hourIds = Array.from(selectedHours).filter((id) => hours.some((h) => h.id === id));
    const processNote = approved
      ? t("hours.approval.bulk.approve_note")
      : (note || t("hours.approval.bulk.reject_note"));

    let completed = 0;
    const remaining = [];
    for (let index = 0; index < hourIds.length; index += 1) {
      const id = hourIds[index];
      try {
        await dispatch(
          updateHourAsync({
            id,
            process: true,
            approved: approved,
            not_approved: !approved,
            process_note: processNote,
          })
        ).unwrap();
        completed += 1;
      } catch (error) {
        remaining.push(...hourIds.slice(index));
        console.error(error);
        break;
      }
    }

    setSelectedHours(new Set(remaining));
    if (remaining.length === 0) {
      setNotApprovedDialogOpen(false);
      toast.success(
        approved
          ? t("hours.approval.bulk.success_approved", { count: completed })
          : t("hours.approval.bulk.success_rejected", { count: completed })
      );
    } else {
      toast.error(
        t("hours.approval.bulk.partial", {
          done: completed,
          remaining: remaining.length,
        })
      );
    }
    load();
    setBulkProcessing(false);
  }, [selectedHours, hours, dispatch, load, bulkProcessing, t]);

  /** Bulk revoke approval: pending again (not the same as «Ikke godkjenn», which sets not_approved). */
  const handleBulkUnapprove = useCallback(async () => {
    if (selectedHours.size === 0) {
      toast.error(t("hours.approval.bulk.select_required"));
      return;
    }
    if (bulkProcessing) return;

    const hourIds = Array.from(selectedHours);
    const toUnapprove = hourIds.filter((id) => hours.find((h) => h.id === id)?.approved);

    if (toUnapprove.length === 0) {
      toast.error(t("hours.approval.bulk_unapprove.no_approved"));
      return;
    }

    setBulkProcessing(true);
    const processNote = t("hours.approval.bulk_unapprove.process_note");

    let completed = 0;
    const remaining = [];
    for (let index = 0; index < toUnapprove.length; index += 1) {
      const id = toUnapprove[index];
      try {
        await dispatch(
          updateHourAsync({
            id,
            process: true,
            approved: false,
            not_approved: false,
            process_note: processNote,
          })
        ).unwrap();
        completed += 1;
      } catch (error) {
        remaining.push(...toUnapprove.slice(index));
        console.error(error);
        break;
      }
    }

    setSelectedHours(new Set(remaining));
    if (remaining.length === 0) {
      toast.success(t("hours.approval.bulk_unapprove.success", { count: completed }));
    } else {
      toast.error(
        t("hours.approval.bulk_unapprove.partial", {
          done: completed,
          remaining: remaining.length,
        })
      );
    }
    load();
    setBulkProcessing(false);
  }, [selectedHours, hours, dispatch, load, bulkProcessing, t]);

  // Handle not approved button click - open dialog
  const handleNotApprovedClick = useCallback(() => {
    if (selectedHours.size === 0) {
      toast.error(t("hours.approval.bulk.select_required"));
      return;
    }
    setNotApprovedDialogOpen(true);
  }, [selectedHours.size, t]);

  // Accordion content component
  const HourAccordionContent = ({ hour, details, isLoading }) => {
    if (isLoading) {
      return (
        <div className="p-4 text-center">
          <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
          <p className="mt-2 text-sm text-gray-500">{t("Laster detaljer...")}</p>
        </div>
      );
    }

    const data = details || hour;
    return (
      <div className="p-4 bg-gray-50 border-t border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Description */}
          {data.description && (
            <div className="col-span-1 md:col-span-2">
              <div className="text-sm font-medium text-gray-700 mb-1">{t("Beskrivelse")}</div>
              <div className="text-sm text-gray-900 whitespace-pre-wrap">{data.description}</div>
            </div>
          )}

          {(data.agreed_overtime_requested || data.agreed_overtime_confirmed) && (
            <div>
              <div className="text-sm font-medium text-gray-700 mb-1">{t("hours.agreed_overtime.section_title")}</div>
              <AgreedOvertimeBadge hour={data} />
              {data.agreed_overtime_note ? (
                <div className="mt-1 text-sm text-gray-600 whitespace-pre-wrap">{data.agreed_overtime_note}</div>
              ) : null}
            </div>
          )}

          {/* Project Details */}
          {data.project && (
            <div>
              <div className="text-sm font-medium text-gray-700 mb-1">{t("Prosjekt")}</div>
              <div className="text-sm text-gray-900">
                {data.project.id ? (
                  <a 
                    href={`/dashboard/projects/${data.project.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 hover:underline"
                  >
                    {data.project.displayTitle || data.project.title || "-"}
                  </a>
                ) : (
                  data.project.displayTitle || data.project.title || "-"
                )}
                {data.project.customer && (
                  <span className="text-gray-500 ml-2">({data.project.customer?.displayTitle || data.project.customer?.title})</span>
                )}
              </div>
            </div>
          )}

          {/* Prosjektaktivitet (linje) — ikke legacy activity_id */}
          {hourHasProjectActivity(data) && (
            <div>
              <div className="text-sm font-medium text-gray-700 mb-1">{t("project_activity.label")}</div>
              <div className="text-sm text-gray-900">
                {projectActivityLine(data.project_activity) ||
                  (data.project_activity_id != null ? `#${data.project_activity_id}` : "—")}
              </div>
            </div>
          )}

          {/* Report */}
          {data.report && (
            <div>
              <div className="text-sm font-medium text-gray-700 mb-1">{t("Rapport")}</div>
              <div className="space-y-2 text-sm text-gray-900">
                {data.report.id ? (
                  <a 
                    href={REPORT_PATH(data.report.id)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-blue-600 hover:text-blue-800 hover:underline font-medium"
                  >
                    {getReportTitle(data.report)}
                  </a>
                ) : (
                  <span className="font-medium">{getReportTitle(data.report)}</span>
                )}
                <div className="flex flex-wrap gap-2">
                  <ModuleStatusBadge status={data.report.status} />
                  <ModuleApprovalBadge
                    approved={data.report.approved}
                    notApproved={data.report.not_approved}
                    approvalStatus={data.report.approval_status}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Work Order */}
          {data.work_order && (
            <div>
              <div className="text-sm font-medium text-gray-700 mb-1">{t("Ordre")}</div>
              <div className="space-y-2 text-sm text-gray-900">
                {data.work_order.id ? (
                  <a 
                    href={WORK_ORDER_PATH(data.work_order.id)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-blue-600 hover:text-blue-800 hover:underline font-medium"
                  >
                    {getWorkOrderTitle(data.work_order)}
                  </a>
                ) : (
                  <span className="font-medium">{getWorkOrderTitle(data.work_order)}</span>
                )}
                <div className="flex flex-wrap gap-2">
                  <ModuleStatusBadge status={data.work_order.status} />
                  <ModuleApprovalBadge
                    approved={data.work_order.is_approved}
                    notApproved={data.work_order.is_rejected}
                    approvalStatus={data.work_order.approval_status}
                  />
                </div>
                {data.work_order.approval_by?.displayTitle && (
                  <div className="text-xs text-gray-500">
                    {t("Behandlet av")}: {data.work_order.approval_by.displayTitle}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Driving Trips */}
          {data.trips && Array.isArray(data.trips) && data.trips.length > 0 && (
            <div className="col-span-1 md:col-span-2">
              <div className="text-sm font-medium text-gray-700 mb-2">{t("Kjøreturer")}</div>
              <div className="space-y-2">
                {data.trips.map((trip) => (
                  <div key={trip.id} className="text-sm text-gray-900 bg-white p-2 rounded border border-gray-200">
                    <div className="flex justify-between">
                      <span>{trip.start_address || trip.from_location || "-"} → {trip.end_address || trip.to_location || "-"}</span>
                      <span className="text-gray-500">
                        {trip.distance_kilometers != null || trip.distance_km != null
                          ? `${trip.distance_kilometers ?? trip.distance_km} km`
                          : ""}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Process Note */}
          {data.process_note && (
            <div className="col-span-1 md:col-span-2">
              <div className="text-sm font-medium text-gray-700 mb-1">{t("Behandlingsnotat")}</div>
              <div className="text-sm text-gray-900 whitespace-pre-wrap">{data.process_note}</div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
    <div className="bg-white rounded-lg shadow">
      <div className="p-6 border-b border-gray-200">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 flex-1">
            <div>
              <EmployeeSelector
                key={employeeId ?? "all"}
                name="user_filter"
                selected={selectedUser}
                filters={[{ name: "employee", value: true }]}
                onChange={(data) => {
                  const obj = data?.target?.object;
                  const user = obj?.id != null ? obj : null;
                  setSelectedUser(user);
                  hydratedEmployeeRef.current = user?.id ?? null;
                  patchApprovalParams({ employee: user?.id ?? null });
                }}
                placeholder={t("Alle ansatte")}
              />
            </div>
            <div className="min-w-0">
              <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:gap-2">
                <div className="min-w-0 flex-1">
                  <ProjectSelector
                    name="approval_project_filter"
                    label="hours.approval.filter_project"
                    noneLabelKey="hours.approval.all_projects"
                    selected={projectFilter || undefined}
                    onChange={(e) => setProjectFilter(e?.target?.object ?? null)}
                    errors={{}}
                  />
                </div>
                {projectFilter && (
                  <button
                    type="button"
                    onClick={() => setProjectFilter(null)}
                    className="shrink-0 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    {t("hours.approval.clear_project_filter")}
                  </button>
                )}
              </div>
            </div>
            <div>
            <PeriodSelector
                  periodType={periodType}
                  baseDate={dateFrom}
                  from={dateFrom}
                  to={dateTo}
                  onChange={({ periodType: nextType, from, to }) => {
                    patchApprovalParams({
                      period: nextType || periodType,
                      date: from,
                      to,
                    });
                  }}
                />
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {(["pending","not_approved","approved","all"]).map((key) => (
              <button
                key={key}
                onClick={() => patchApprovalParams({ view: key })}
                className={`px-3 py-2 rounded-lg text-sm font-medium ${viewFilter===key?"bg-blue-600 text-white":"bg-gray-200 text-gray-700 hover:bg-gray-300"}`}
              >
                {key === "pending" && t("Til godkjenning")}
                {key === "not_approved" && t("Ikke godkjent")}
                {key === "approved" && t("Godkjent")}
                {key === "all" && t("Alle")}
              </button>
            ))}
            <div
              className="flex items-center gap-2 sm:ml-1 sm:pl-3 sm:border-l sm:border-gray-200"
              role="group"
              aria-label={t("hours.sheet.export_aria")}
            >
              <button
                type="button"
                disabled={!!exporting}
                onClick={() => handleExport("pdf")}
                className="px-3 py-2 rounded-lg text-sm font-medium bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {exporting === "pdf" ? t("hours.sheet.export_pdf_loading") : t("hours.sheet.export_pdf")}
              </button>
              <button
                type="button"
                disabled={!!exporting}
                onClick={() => handleExport("excel")}
                className="px-3 py-2 rounded-lg text-sm font-medium bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {exporting === "excel"
                  ? t("hours.sheet.export_excel_loading")
                  : t("hours.sheet.export_excel")}
              </button>
            </div>
          </div>
        </div>
        {/* Bulk actions */}
        {canReconcile && <div className="mt-4 flex flex-wrap items-center gap-3">
          <HourBillingButton hourIds={filtered.filter((hour) => selectedHours.has(hour.id)).map((hour) => hour.id)} onSaved={load} disabled={bulkProcessing || isLoading} showSelectionHint />
        </div>}
        {(canProcess || canReconcile) && selectedHours.size > 0 && (
          <div className="mt-4 flex flex-wrap items-center gap-2 pt-4 border-t border-gray-200">
            <span className="text-sm text-gray-700">{t("Valgt")}: {selectedHours.size}</span>
            {canBulkReassign && (
              <button
                type="button"
                onClick={() => setBulkReassignOpen(true)}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700"
              >
                {t("hours.bulk_reassign.open")}
              </button>
            )}
            {canProcess && <>
            <button
              onClick={() => handleBulkApprove(true)}
              disabled={bulkProcessing}
              className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {bulkProcessing && (
                <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              )}
              {t("Godkjenn valgte")}
            </button>
            <button
              type="button"
              onClick={handleBulkUnapprove}
              disabled={bulkProcessing}
              className="px-4 py-2 bg-amber-600 text-white rounded-lg text-sm font-medium hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {bulkProcessing && (
                <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              )}
              {t("hours.approval.bulk_unapprove.action")}
            </button>
            <button
              onClick={handleNotApprovedClick}
              disabled={bulkProcessing}
              className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {t("Ikke godkjenn valgte")}
            </button>
            </>}
          </div>
        )}
      </div>

      <div className="p-6">
        {isLoading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-4 text-sm text-gray-500">{t("Laster timer...")}</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center text-gray-500 py-8">{t("Ingen timer i valgt filter")}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {(canProcess || canReconcile) && (
                    <th className="px-4 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={selectedHours.size === filtered.length && filtered.length > 0}
                        onChange={toggleAllSelection}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </th>
                  )}
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"></th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t("Dato")}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t("Ansatt")}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t("Prosjekt / Aktivitet")}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t("Timer")}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t("Status")}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t("Tripletex")}</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filtered.map((h) => {
                  const isExpanded = expandedHours.has(h.id);
                  const details = hourDetails[h.id];
                  return (
                    <Fragment key={h.id}>
                      <tr className={`hover:bg-gray-50 ${h.approved ? "border-l-4 border-green-400" : h.not_approved ? "border-l-4 border-red-400" : ""}`}>
                        {(canProcess || canReconcile) && (
                          <td className="px-4 py-3">
                            <input
                              type="checkbox"
                              checked={selectedHours.has(h.id)}
                              onChange={() => toggleSelection(h.id)}
                              onClick={(e) => e.stopPropagation()}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                          </td>
                        )}
                        <td className="px-4 py-3">
                          <button
                            type="button"
                            onClick={() => toggleAccordion(h)}
                            className="text-gray-400 hover:text-gray-600"
                          >
                            {isExpanded ? (
                              <ChevronUpIcon className="h-5 w-5" />
                            ) : (
                              <ChevronDownIcon className="h-5 w-5" />
                            )}
                          </button>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">{format(new Date(h.date), "d. MMM yyyy", { locale: currentDateFnsLocale() })}</td>
                        <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">{h.user?.displayTitle || "-"}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          <div className="font-medium">{h.project?.displayTitle || h.project?.title || t("Uten prosjekt")}</div>
                          <div className="text-gray-600 text-xs">
                            {projectActivityLine(h.project_activity) || t("project_activity.missing")}
                          </div>
                          {(h.report || h.work_order) && (
                            <div className="mt-2 flex flex-wrap gap-2">
                              {h.report && (
                                h.report.id ? (
                                  <a
                                    href={REPORT_PATH(h.report.id)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex max-w-full items-center rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-200"
                                  >
                                    {`Rapport: ${getReportTitle(h.report)}`}
                                  </a>
                                ) : (
                                  <span className="inline-flex max-w-full items-center rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">
                                    {`Rapport: ${getReportTitle(h.report)}`}
                                  </span>
                                )
                              )}
                              {h.work_order && (
                                h.work_order.id ? (
                                  <a
                                    href={WORK_ORDER_PATH(h.work_order.id)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex max-w-full items-center rounded-full bg-stone-100 px-2.5 py-1 text-xs font-medium text-stone-700 hover:bg-stone-200"
                                  >
                                    {`Ordre: ${getWorkOrderTitle(h.work_order)}`}
                                  </a>
                                ) : (
                                  <span className="inline-flex max-w-full items-center rounded-full bg-stone-100 px-2.5 py-1 text-xs font-medium text-stone-700">
                                    {`Ordre: ${getWorkOrderTitle(h.work_order)}`}
                                  </span>
                                )
                              )}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                          <div className="flex flex-col items-start gap-1">
                            <span>{(parseFloat(h.time) || 0).toFixed(1)}h</span>
                            <AgreedOvertimeBadge hour={h} />
                            <HourBillingBadges hour={h} />
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap"><StatusBadge status={h.status} /></td>
                        <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap"><TripletexSyncBadge hour={h} /></td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          {canProcess && (
                            <div className="flex items-center justify-end gap-2">
                              <button
                                type="button"
                                className="text-xs text-slate-700 hover:text-slate-900 bg-slate-100 hover:bg-slate-200 px-3 py-1.5 rounded"
                                onClick={() => { setSelectedHour(h); setEditOpen(true); }}
                              >
                                {t("Rediger")}
                              </button>
                              <button
                                type="button"
                                className="text-xs text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 px-3 py-1.5 rounded"
                                onClick={() => { setSelectedHour(h); setProcessOpen(true); }}
                              >
                                {t("Behandle")}
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr>
                          <td colSpan={canProcess ? 9 : 8} className="p-0">
                            <HourAccordionContent hour={h} details={details} isLoading={loadingDetails.has(h.id)} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {canProcess && (
        <>
          <ApproveHourDialog
            hour={selectedHour}
            open={processOpen}
            onClose={() => { setProcessOpen(false); setSelectedHour(null); }}
            onRefresh={handleHourUpdated}
          />
          <HourEditDialog
            open={editOpen}
            hourId={selectedHour?.id}
            onClose={() => { setEditOpen(false); setSelectedHour(null); }}
            onSaved={handleHourEditSaved}
            onDeleted={handleHourDeleted}
          />
          <HoursBulkReassignDialog
            open={bulkReassignOpen}
            onClose={() => setBulkReassignOpen(false)}
            hourIds={Array.from(selectedHours)}
            initialProject={null}
            onDone={() => {
              setSelectedHours(new Set());
              load();
            }}
          />
          <PMDialog
            open={notApprovedDialogOpen}
            onClose={() => {
              setNotApprovedDialogOpen(false);
            }}
            title={t("Ikke godkjenn timer")}
          >
            <Formik
              initialValues={{ note: "" }}
              validationSchema={Yup.object({
                note: Yup.string().required(t("Notat er påkrevd")),
              })}
              onSubmit={(values, { setSubmitting }) => {
                handleBulkApprove(false, values.note);
                setSubmitting(false);
              }}
            >
              {(formik) => (
                <Form>
                  <div className="space-y-4">
                    <div>
                      <PMField
                        name="note"
                        label={t("Notat")}
                        multiline={true}
                        rows={4}
                        placeholder={t("Skriv et notat om hvorfor timene ikke godkjennes...")}
                        {...formik}
                      />
                    </div>
                    <div className="flex justify-end gap-2 pt-4">
                      <PMButton
                        type="button"
                        onClick={() => {
                          setNotApprovedDialogOpen(false);
                          formik.resetForm();
                        }}
                        className="bg-gray-400 hover:bg-gray-500"
                      >
                        {t("Avbryt")}
                      </PMButton>
                      <PMButton
                        type="submit"
                        disabled={bulkProcessing || !formik.values.note}
                        className="bg-red-600 hover:bg-red-700"
                      >
                        {bulkProcessing ? (
                          <span className="flex items-center gap-2">
                            <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            {t("Behandler...")}
                          </span>
                        ) : (
                          t("Ikke godkjenn")
                        )}
                      </PMButton>
                    </div>
                  </div>
                </Form>
              )}
            </Formik>
          </PMDialog>
        </>
      )}
    </div>

    {canProcess && approvalSelectionSummary && (
      <aside
        className="fixed right-4 top-24 z-50 flex w-[min(22rem,calc(100vw-2rem))] max-h-[min(32rem,calc(100vh-7rem))] flex-col rounded-xl border border-gray-200 bg-white p-4 shadow-xl"
        aria-label={t("hours.approval.selection_summary.title")}
        aria-live="polite"
      >
        <div className="flex shrink-0 items-start justify-between gap-2 border-b border-gray-100 pb-3">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">{t("hours.approval.selection_summary.title")}</h3>
            <p className="mt-1 text-xs text-gray-500">{t("hours.approval.selection_summary.subtitle")}</p>
          </div>
          <button
            type="button"
            onClick={() => setSelectedHours(new Set())}
            className="shrink-0 rounded-md px-2 py-1 text-xs font-medium text-blue-700 hover:bg-blue-50"
          >
            {t("hours.approval.selection_summary.clear")}
          </button>
        </div>
        <ul className="mt-3 min-h-0 flex-1 space-y-4 overflow-y-auto pr-0.5">
          {approvalSelectionSummary.users.map((u) => (
            <li key={String(u.userId)} className="rounded-lg bg-gray-50 p-3 text-sm">
              <div className="font-medium text-gray-900">{u.title}</div>
              <div className="mt-2 space-y-1 text-xs text-gray-700">
                <div>
                  {t("hours.approval.selection_summary.total_selected", {
                    hours: u.selectedHours.toFixed(1),
                    entries: u.selectedEntries,
                  })}
                </div>
                <div>
                  {t("hours.approval.selection_summary.period_totals", {
                    hours: u.periodHours.toFixed(1),
                    entries: u.periodEntries,
                  })}
                </div>
                {u.otherDays > 0 && (
                  <div className="text-gray-600">
                    {t("hours.approval.selection_summary.other_days", {
                      days: u.otherDays,
                      hours: u.otherHours.toFixed(1),
                    })}
                  </div>
                )}
              </div>
              {u.dayRows.length > 0 && (
                <div className="mt-2 border-t border-gray-200 pt-2">
                  <div className="text-xs font-medium text-gray-600">{t("hours.approval.selection_summary.by_date")}</div>
                  <ul className="mt-1 space-y-1">
                    {u.dayRows.map((d) => (
                      <li key={d.dayKey} className="text-xs text-gray-700">
                        {t("hours.approval.selection_summary.day_row", {
                          date: d.dateLabel,
                          selected: d.selectedHours.toFixed(1),
                          registered: d.registeredHours.toFixed(1),
                        })}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </li>
          ))}
        </ul>
        <div className="mt-3 shrink-0 space-y-2 border-t border-gray-200 pt-3">
          <div className="text-xs font-medium text-gray-700">
            {t("Valgt")}: {selectedHours.size}
          </div>
          {canBulkReassign && (
            <button
              type="button"
              onClick={() => setBulkReassignOpen(true)}
              className="w-full rounded-lg bg-indigo-600 px-3 py-2 text-center text-sm font-medium text-white hover:bg-indigo-700"
            >
              {t("hours.bulk_reassign.open")}
            </button>
          )}
          <button
            type="button"
            onClick={() => handleBulkApprove(true)}
            disabled={bulkProcessing}
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-green-600 px-3 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {bulkProcessing && (
              <span className="inline-block h-4 w-4 animate-spin rounded-full border-b-2 border-white" />
            )}
            {t("Godkjenn valgte")}
          </button>
          <button
            type="button"
            onClick={handleBulkUnapprove}
            disabled={bulkProcessing}
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-amber-600 px-3 py-2 text-sm font-medium text-white hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {bulkProcessing && (
              <span className="inline-block h-4 w-4 animate-spin rounded-full border-b-2 border-white" />
            )}
            {t("hours.approval.bulk_unapprove.action")}
          </button>
          <button
            type="button"
            onClick={handleNotApprovedClick}
            disabled={bulkProcessing}
            className="w-full rounded-lg bg-red-600 px-3 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {t("Ikke godkjenn valgte")}
          </button>
        </div>
      </aside>
    )}
    </>
  );
};


